import React from 'react';
import { observer } from 'mobx-react-lite';
import styled, { ThemeProps } from 'styled-components';
import { H1, H1Big, TextLG, TextMD, TextSMMedium, TextXSMedium } from 'styleguide/Texts';
import { colors } from 'styleguide/colors';
import { ThemedHevyProLogo } from 'components/svg/ThemedHevyProLogo';
import { PrimaryButton } from 'styleguide/Buttons';
import { DividerLine } from 'styleguide/DividerLine';
import CheckMarkBlue from 'components/assets/check-blue.svg';
import MastercardLogo from '../Paywall/components/assets/Mastercard.svg';
import StripeLogo from '../Paywall/components/assets/Stripe.svg';
import VisaLogo from '../Paywall/components/assets/Visa.svg';
import ProductHuntLogo from '../Paywall/components/assets/ProductHunt.svg';
import AppsWeLoveLogo from '../Paywall/components/assets/AppsWeLove.svg';
import LifewireLogo from '../Paywall/components/assets/Lifewire.svg';
import { UnauthenticatedPaywallViewModel } from './UnauthenticatedPaywallViewModel';
import { Theme } from 'styleguide/types';
import { FAQCard } from 'screens/Paywall/components/FAQCard';
import { PaywallCard } from 'screens/Paywall/components/PaywallCard';
import { ReviewCard } from 'screens/Paywall/components/ReviewCard';
import { SwipeableCards } from 'screens/Paywall/components/SwipeableCards';
import { Ratings } from 'screens/Paywall/components/Ratings';

const MOBILE_BREAKPOINT = 1270;

const Content = styled.div`
  margin: 20px 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const CardContainer = styled.div`
  display: grid;
  grid-template-columns: 470px 470px;
  column-gap: 40px;
  margin-bottom: 24;
  grid-auto-flow: dense;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 360px;
  }
`;

const CardLeft = styled.div`
  padding: 40px;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  background-color: ${(props: ThemeProps<Theme>) =>
    props.theme.name === 'light' ? colors.neutral100 : colors.neutral700};
  border-radius: 8px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-column: 1;
    grid-row: 4;
    padding: 20px;
    align-items: center;
  }
`;

const CardRight = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-column: 1;
  }
`;

const PlanComparisonTable = styled.table`
  border-collapse: collapse;
  width: 100%;
`;

const PlanComparisonItem = styled.tr`
  height: 44px;
  border-top: 1px solid ${colors.neutral200};
`;

const ItemName = ({ children }: { children: React.ReactNode }) => {
  return (
    <td>
      <TextSMMedium>{children}</TextSMMedium>
    </td>
  );
};

const ItemPlanContainer = styled.td`
  padding: 0 16px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0 4px;
  }
`;

const ItemPlan = ({ children }: { children: React.ReactNode }) => {
  return (
    <ItemPlanContainer>
      <TextSMMedium type="secondary">{children}</TextSMMedium>
    </ItemPlanContainer>
  );
};

const BottomContent = styled.div`
  width: 100%;
  max-width: 980px;
  flex-direction: row;
  text-align: center;
`;

const PaymentMethods = styled.div`
  margin-bottom: 20px;
  display: grid;
  grid-template-columns: auto auto auto auto auto;
  gap: 8px;
  align-items: center;
  justify-content: center;
`;

const UserReviews = styled.div`
  margin-bottom: 96px;
  display: grid;
  grid-template-columns: 400px 400px 400px;
  gap: 40px;
  justify-content: center;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const ReviewCardMobileContainer = styled.div`
  cursor: pointer;
  user-select: none;
  height: 400px;
  margin: 0 8px;
`;

const MobileContent = styled.div`
  display: none;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: inherit;
  }
`;

const FeaturedContainer = styled.div`
  margin-bottom: 20px;
  display: grid;
  grid-template-columns: auto auto auto;
  column-gap: 96px;
  row-gap: 64px;
  align-items: center;
  justify-content: center;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: auto;
    margin-bottom: 0;
  }
`;

const FeaturedLogoContainer = styled.div`
  margin-left: auto;
  margin-right: auto;
`;

const FAQList = styled.div`
  width: 100%;
  height: 560px;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const getUserReviews = () => {
  const reviews: {
    title: string;
    date: string;
    author: string;
    description: string;
  }[] = [
    {
      title: 'Best workout tracker!',
      date: 'March 16, 2022',
      author: 'Rpds',
      description:
        'The best workout tracker I was able to find on the market. Love the aesthetics, nothing missing from the functionality for me and even if you manage to find anything, there is exemplary customer support, so you will surely be listened to 🙂',
    },
    {
      title: 'The easiest workout app!',
      date: 'March 16, 2022',
      author: 'Doogiepls',
      description:
        'I am so impressed by what this app does, but more importantly how they have made it so easy to enter and edit workouts. It just makes a lot of sense and the app stays out of your way while you are working out. Perfect amount of functionality.',
    },
    {
      title: 'The Best One',
      date: 'March 16, 2022',
      author: 'Tigriffe',
      description:
        'After few months using it and with the new functionality, this is the best app on the App Store for tracking weight lifting right now. And the team is really cool!',
    },
    {
      title: 'The workout app I’ve been waiting for!',
      date: 'March 16, 2022',
      author: 'Dannybings',
      description:
        'In the same way Strava revolutionized my running routines, this is transforming my indoor gym routines... And there’s nothing like generating and sharing workouts with a motivated and inspirational community. Highly recommend!',
    },
    {
      title: '6 stars?',
      date: 'March 16, 2022',
      author: 'Nabil',
      description:
        'Best software gym logger ive used. Devs provide great support. not buggy like a lot of other apps ive used! Can I rate it 6stars?',
    },
    {
      title: 'I love Hevy!',
      date: 'March 16, 2022',
      author: 'natzgrierson',
      description:
        'This is by far the best weight training tracker app I have ever used. I downloaded so many (even paid for one) and Hevy was the best for so many different reasons! Love the community on here too :)',
    },
    {
      title: 'Great Workout Tracker',
      date: 'March 16, 2022',
      author: 'Nellboy_uk',
      description:
        'I’ve loved this app from the start. It has been a great way to track my progression and change exercises to hit certain muscle groups. Setup different workouts for your week and change them around with ease. Highly recommended 👍',
    },
  ];

  return reviews.sort(() => Math.random() - 0.5);
};

const renderFAQs = (vm: UnauthenticatedPaywallViewModel) => {
  const faqs: {
    title: string;
    text: string;
  }[] = [
    {
      title: 'What does Hevy Pro include?',
      text: 'Hevy Pro includes full access to all Pro features. Create unlimited routines, create unlimited custom exercises, get unlimited graph history, and log all body measurements.',
    },
    {
      title: 'Can I cancel my subscription anytime?',
      text: 'Yes, you can cancel your Hevy Pro monthly or yearly subscription whenever you want! You will continue to have access to Hevy Pro until your subscription expires.',
    },
    {
      title: 'If I subscribe online, can I use the mobile app?',
      text: 'Yes, absolutely. Just sign in to the same account and you will have full access to Hevy Pro.',
    },
    {
      title: 'What happens if I switch devices or platforms?',
      text: 'You can sign into your account across platforms with the same user name and password. The subscription is connected to your account, not the platform you are using. You can be signed into two different devices simultaneously and even two different platforms at the same time.',
    },
    {
      title: 'How does the Hevy Pro Lifetime plan work?',
      text: 'If you are already subscribed to a monthly or yearly plan and want to switch to a lifetime plan, make sure to cancel your monthly or yearly subscription after you purchase the lifetime plan to avoid any recurring charges.',
    },
    {
      title: 'Can I switch subscription plans?',
      text: 'Yes, absolutely. You are able to change subscription plans at any time through the "Manage Subscription" tab in Hevy settings. You also have the option to switch to the Lifetime plan, see our lifetime FAQ on how to do that.',
    },
    {
      title: 'Is it possible to get a refund?',
      text: "Yes, if you're not happy with Hevy Pro contact support within 14 days of your purchase to qualify for a refund. Contact hello@hevyapp for any help.",
    },
  ];

  return faqs.map((f, index) => (
    <FAQCard
      key={`faq-${index}`}
      title={f.title}
      text={f.text}
      isFirst={index === 0}
      isOpened={index === vm.openedFaqIndex}
      onPress={() => vm.onFAQPress(index)}
    />
  ));
};

export class UnauthenticatedPaywall extends React.Component {
  vm: UnauthenticatedPaywallViewModel;

  constructor(props: any) {
    super(props);
    this.vm = new UnauthenticatedPaywallViewModel();
  }

  render() {
    return <PaywallInner vm={this.vm} />;
  }
}

const PaywallInner = observer(({ vm }: { vm: UnauthenticatedPaywallViewModel }) => {
  const userReviews = getUserReviews();

  return (
    <Content>
      <H1Big style={{ textAlign: 'center', marginBottom: 16 }}>Pick your Hevy plan</H1Big>
      <TextMD style={{ textAlign: 'center', marginBottom: 48 }}>
        No commitments. Cancel anytime.
      </TextMD>
      <CardContainer style={{ marginBottom: 16 }}>
        <CardLeft>
          <ThemedHevyProLogo />
          <TextLG style={{ textAlign: 'center', marginTop: 20, marginBottom: 32 }}>
            Take your training to the next level
          </TextLG>
          <PlanComparisonTable>
            <PlanComparisonItem style={{ border: 'none' }}>
              <td></td>
              <ItemPlanContainer>
                <TextSMMedium>Free</TextSMMedium>
              </ItemPlanContainer>
              <ItemPlanContainer>
                <TextLG style={{ color: '#F7D000', fontSize: 16 }}>PRO</TextLG>
              </ItemPlanContainer>
            </PlanComparisonItem>
            <PlanComparisonItem>
              <ItemName>Log Unlimited Workouts</ItemName>
              <ItemPlan>
                <CheckMarkBlue />
              </ItemPlan>
              <ItemPlan>
                <CheckMarkBlue />
              </ItemPlan>
            </PlanComparisonItem>
            <PlanComparisonItem>
              <ItemName>Unlimited Routines</ItemName>
              <ItemPlan>4 max</ItemPlan>
              <ItemPlan>
                <CheckMarkBlue />
              </ItemPlan>
            </PlanComparisonItem>
            <PlanComparisonItem>
              <ItemName>Unlimited Custom Exercises</ItemName>
              <ItemPlan>7 max</ItemPlan>
              <ItemPlan>
                <CheckMarkBlue />
              </ItemPlan>
            </PlanComparisonItem>
            <PlanComparisonItem>
              <ItemName>Measurement Tracking</ItemName>
              <ItemPlan>Limited</ItemPlan>
              <ItemPlan>
                <CheckMarkBlue />
              </ItemPlan>
            </PlanComparisonItem>
            <PlanComparisonItem>
              <ItemName>Unlimited Graph History</ItemName>
              <ItemPlan>3 months</ItemPlan>
              <ItemPlan>
                <CheckMarkBlue />
              </ItemPlan>
            </PlanComparisonItem>
          </PlanComparisonTable>
        </CardLeft>
        <CardRight>
          {vm.products.map(p => (
            <PaywallCard
              key={p.key}
              planName={p.title}
              description={p.description}
              hint={p.hint}
              price={p.price}
              discountedPrice={p.discountedPrice}
              selected={p.isSelected}
              onClick={p.onClick}
              style={{ marginBottom: 16 }}
            />
          ))}
          <PrimaryButton
            style={{ width: '100%', height: 48, marginBottom: 16 }}
            type="submit"
            title={vm.ctaTitle}
            onClick={vm.onCtaClick}
          />
        </CardRight>
        <div />
      </CardContainer>
      <BottomContent>
        <DividerLine />
        <TextXSMedium type="secondary" style={{ marginTop: 12, marginBottom: 16 }}>
          Cancel your subscription at any time. Subscriptions will automatically renew unless
          cancelled within 24 hours before the end of the current period. You can cancel anytime
          with your iTunes account settings. Any unused portion of a free trial will be forfeited if
          you purchase a subscription.
        </TextXSMedium>
        <PaymentMethods>
          <MastercardLogo />
          <StripeLogo />
          <VisaLogo />
        </PaymentMethods>
        <H1 style={{ flex: 1, textAlign: 'center', marginTop: 96 }}>Athletes love Hevy</H1>
        <Ratings />
        <UserReviews>
          {userReviews.slice(0, 3).map((r, index) => (
            <ReviewCard
              key={`review-${index}`}
              title={r.title}
              date={r.date}
              author={r.author}
              description={r.description}
            />
          ))}
        </UserReviews>
        <MobileContent style={{ marginBottom: 64, marginLeft: -16, marginRight: -16 }}>
          <SwipeableCards
            items={userReviews.slice(0, 4)}
            renderItem={r => (
              <ReviewCardMobileContainer>
                <ReviewCard
                  title={r.title}
                  date={r.date}
                  author={r.author}
                  description={r.description}
                />
              </ReviewCardMobileContainer>
            )}
            navBulletStyle={isSelected => ({
              width: 12,
              height: 12,
              borderRadius: 12,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: colors.primary500,
              backgroundColor: isSelected ? colors.primary500 : colors.white,
            })}
          />
        </MobileContent>
        <TextLG style={{ flex: 1, textAlign: 'center', marginBottom: 64 }}>
          ...and featured by many!
        </TextLG>
        <FeaturedContainer>
          <FeaturedLogoContainer>
            <ProductHuntLogo />
          </FeaturedLogoContainer>
          <FeaturedLogoContainer>
            <AppsWeLoveLogo />
          </FeaturedLogoContainer>
          <FeaturedLogoContainer>
            <LifewireLogo />
          </FeaturedLogoContainer>
        </FeaturedContainer>
        <H1 style={{ flex: 1, textAlign: 'center', marginTop: 96, marginBottom: 48 }}>
          Any questions?
        </H1>
        <FAQList>{renderFAQs(vm)}</FAQList>
      </BottomContent>
    </Content>
  );
});
